import { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useSolarSimulation, type SimulationResult, type HourlyGeneration } from '../hooks/useSolarSimulation';
import { formatPower, formatEnergy } from '../utils/solarCalculations';
import type { ProjectConfig, Project } from '../utils/projects';
import { ShareButton } from './ShareButton';
import './SolarSimulator.css';

export interface SolarSimulatorProps {
  lat: number;
  lon: number;
  projectConfig?: ProjectConfig | null;
  currentProject?: Project | null;
  siteUrl?: string;
  onSimulationUpdate?: (result: SimulationResult | null) => void;
}

type TimeView = 'day' | 'week' | 'month';

export function SolarSimulator({ lat, lon, projectConfig, currentProject, siteUrl, onSimulationUpdate }: SolarSimulatorProps) {
  // Simulation parameters - initialized from project config
  const [area, setArea] = useState(projectConfig?.farm_area_acres ?? 2700);
  const [efficiency, setEfficiency] = useState<'high' | 'average' | 'old'>(projectConfig?.panel_efficiency ?? 'average');
  const [inverterLoss, setInverterLoss] = useState(projectConfig?.inverter_loss ?? 0.03);
  const [soilingLoss, setSoilingLoss] = useState(projectConfig?.soiling_loss ?? 0.02);
  const [tempDerate, setTempDerate] = useState(projectConfig?.temperature_derate ?? 0.004);
  const [timeView, setTimeView] = useState<TimeView>('day');
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackHour, setPlaybackHour] = useState<number | null>(null);
  const [needsRun, setNeedsRun] = useState(true);

  const { simulation, loading, error, runSimulation } = useSolarSimulation();
  const chartRef = useRef<HTMLDivElement>(null);
  const playbackRef = useRef<number | null>(null);

  // Update parameters when project config changes
  useEffect(() => {
    if (projectConfig) {
      setArea(projectConfig.farm_area_acres);
      setEfficiency(projectConfig.panel_efficiency);
      setInverterLoss(projectConfig.inverter_loss);
      setSoilingLoss(projectConfig.soiling_loss);
      setTempDerate(projectConfig.temperature_derate);
      setNeedsRun(true);
    }
  }, [projectConfig]);

  // Auto-run simulation when location or config changes
  useEffect(() => {
    if (needsRun && lat && lon) {
      runSimulation({
        lat,
        lon,
        farmAreaAcres: area,
        panelEfficiency: efficiency,
        inverterLoss,
        soilingLoss,
        temperatureDerate: tempDerate,
      });
      setNeedsRun(false);
    }
  }, [needsRun, lat, lon, area, efficiency, inverterLoss, soilingLoss, tempDerate, runSimulation]);

  // Notify parent of simulation updates
  useEffect(() => {
    onSimulationUpdate?.(simulation);
  }, [simulation, onSimulationUpdate]);

  // Handle playback animation
  useEffect(() => {
    if (isPlaying && simulation) {
      playbackRef.current = window.setInterval(() => {
        setPlaybackHour(prev => {
          const next = (prev ?? -1) + 1;
          if (next >= simulation.hourly_generation.length) {
            setIsPlaying(false);
            return null;
          }
          return next;
        });
      }, 500);
    } else {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    }

    return () => {
      if (playbackRef.current) {
        clearInterval(playbackRef.current);
      }
    };
  }, [isPlaying, simulation]);

  // Draw chart with D3
  useEffect(() => {
    if (!simulation || !chartRef.current) return;

    const data = simulation.hourly_generation;
    const currentHourIndex = getCurrentHourIndex(data);
    drawChart(chartRef.current, data, playbackHour, currentHourIndex);
  }, [simulation, playbackHour, timeView]);

  const togglePlayback = () => {
    if (isPlaying) {
      setIsPlaying(false);
      setPlaybackHour(null);
    } else {
      setPlaybackHour(0);
      setIsPlaying(true);
    }
  };

  const handleRunSimulation = () => {
    runSimulation({
      lat,
      lon,
      farmAreaAcres: area,
      panelEfficiency: efficiency,
      inverterLoss,
      soilingLoss,
      temperatureDerate: tempDerate,
    });
  };

  // Get current display values - show REALTIME when not playing
  const getCurrentDisplayValues = () => {
    if (!simulation) {
      return { power: 0, efficiency: 0, energy: 0, homes: 0 };
    }

    // If playing animation, show playback values
    if (playbackHour !== null && simulation.hourly_generation[playbackHour]) {
      const hourData = simulation.hourly_generation[playbackHour];
      return {
        power: hourData.power_mw,
        efficiency: hourData.efficiency_factor,
        energy: simulation.daily_energy_mwh,
        homes: simulation.homes_powered
      };
    }

    // Otherwise show REALTIME current values
    return {
      power: simulation.current_power_mw,
      efficiency: simulation.current_efficiency,
      energy: simulation.daily_energy_mwh,
      homes: simulation.homes_powered
    };
  };

  const displayValues = getCurrentDisplayValues();

  return (
    <div className="simulator">
      <div className="simulator-header">
        <h2>Solar Generation Simulator</h2>
        <p className="simulator-subtitle">Live weather-driven solar farm simulation</p>
      </div>

      <div className="simulator-body">
        <div className="simulator-display">
          {loading && <div className="loading-overlay">Simulating...</div>}
          {error && <div className="error-message">{error}</div>}

          {simulation && (
            <>
              <div className="live-stats">
                <div className="stat-card primary">
                  <div className="stat-content">
                    <span className="stat-value">{formatPower(displayValues.power)}</span>
                    <span className="stat-label">
                      {playbackHour !== null ? 'Power at Time' : 'Current Power'}
                    </span>
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-content">
                    <span className="stat-value">{formatEnergy(displayValues.energy)}</span>
                    <span className="stat-label">Daily Energy</span>
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-content">
                    <span className="stat-value">{(displayValues.efficiency * 100).toFixed(0)}%</span>
                    <span className="stat-label">Efficiency</span>
                  </div>
                </div>
                
                <div className="stat-card">
                  <div className="stat-content">
                    <span className="stat-value">{displayValues.homes.toLocaleString()}</span>
                    <span className="stat-label">Homes Powered</span>
                  </div>
                </div>
              </div>

              <div className="chart-container">
                <div className="chart-header">
                  <div className="time-controls">
                    <button
                      className={timeView === 'day' ? 'active' : ''}
                      onClick={() => setTimeView('day')}
                    >
                      Day
                    </button>
                    <button
                      className={timeView === 'week' ? 'active' : ''}
                      onClick={() => setTimeView('week')}
                      disabled
                    >
                      Week
                    </button>
                    <button
                      className={timeView === 'month' ? 'active' : ''}
                      onClick={() => setTimeView('month')}
                      disabled
                    >
                      Month
                    </button>
                  </div>
                  
                  <button className="playback-btn" onClick={togglePlayback}>
                    {isPlaying ? 'Stop' : 'Play'} Animation
                  </button>
                </div>
                
                <div ref={chartRef} className="chart" />
                
                {playbackHour !== null && simulation.hourly_generation[playbackHour] && (
                  <div className="playback-info">
                    <span className="playback-time">
                      {new Date(simulation.hourly_generation[playbackHour].timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="playback-weather">
                      {simulation.hourly_generation[playbackHour].weather_description}
                    </span>
                  </div>
                )}

                {playbackHour === null && (
                  <div className="realtime-indicator">
                    <span className="realtime-dot"></span>
                    <span>Showing current realtime values</span>
                  </div>
                )}
              </div>

              {/* Nameplate Capacity Banner */}
              <div className="capacity-banner">
                <div className="capacity-item">
                  <span className="cap-label">Nameplate Capacity</span>
                  <span className="cap-value">{simulation.nameplate_capacity_mw?.toFixed(0) || '—'} MW</span>
                  <span className="cap-note">Theoretical max (STC)</span>
                </div>

                {/* Share Button between capacity items */}
                <div className="share-button-wrapper">
                  <ShareButton
                    simulation={simulation}
                    currentProject={currentProject}
                    siteUrl={siteUrl || window.location.origin}
                  />
                </div>

                <div className="capacity-item">
                  <span className="cap-label">Current Output</span>
                  <span className="cap-value">{simulation.peak_power_mw?.toFixed(0) || '—'} MW</span>
                  <span className="cap-note">Peak today ({((simulation.peak_power_mw / (simulation.nameplate_capacity_mw || 1)) * 100).toFixed(0)}% of capacity)</span>
                </div>
              </div>

              <div className="comparison-row">
                <div className="comparison-item good">
                  <span className="comp-label">Best Dec Day</span>
                  <span className="comp-value">{formatEnergy(simulation.good_day_energy_mwh)}</span>
                </div>
                <div className="comparison-item current">
                  <span className="comp-label">Today's Forecast</span>
                  <span className="comp-value">{formatEnergy(simulation.daily_energy_mwh)}</span>
                </div>
                <div className="comparison-item poor">
                  <span className="comp-label">Worst Dec Day</span>
                  <span className="comp-value">{formatEnergy(simulation.bad_day_energy_mwh)}</span>
                </div>
              </div>

              {/* Monthly Context */}
              {simulation.monthly_estimate && (
                <div className="monthly-context">
                  <h4>December UK Averages (PVGIS Data)</h4>
                  <div className="monthly-stats">
                    <div className="monthly-stat">
                      <span className="ms-value">{simulation.monthly_estimate.capacity_factor_percent}%</span>
                      <span className="ms-label">Capacity Factor</span>
                    </div>
                    <div className="monthly-stat">
                      <span className="ms-value">{simulation.monthly_estimate.peak_sun_hours} hrs</span>
                      <span className="ms-label">Peak Sun Hours</span>
                    </div>
                    <div className="monthly-stat">
                      <span className="ms-value">{formatEnergy(simulation.monthly_estimate.daily_average_mwh)}</span>
                      <span className="ms-label">Avg Daily</span>
                    </div>
                    <div className="monthly-stat">
                      <span className="ms-value">{simulation.monthly_estimate.homes_powered_avg.toLocaleString()}</span>
                      <span className="ms-label">Homes (avg)</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Run button to refresh simulation */}
              <div className="run-section">
                <button className="run-btn" onClick={handleRunSimulation} disabled={loading}>
                  {loading ? 'Simulating...' : 'Refresh Simulation'}
                </button>
              </div>
            </>
          )}

          {!simulation && !loading && (
            <div className="no-simulation">
              <p>Click the button below to run the simulation</p>
              <button className="run-btn" onClick={handleRunSimulation}>
                Run Simulation
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Get index of current hour in the data
function getCurrentHourIndex(data: HourlyGeneration[]): number {
  const now = new Date();
  const currentHour = now.getHours();
  
  for (let i = 0; i < data.length; i++) {
    const dataHour = new Date(data[i].timestamp).getHours();
    if (dataHour === currentHour) {
      return i;
    }
  }
  return -1;
}

// D3 Chart Drawing Function
function drawChart(
  container: HTMLDivElement, 
  data: HourlyGeneration[], 
  highlightIndex: number | null,
  currentHourIndex: number
) {
  // Clear previous chart
  d3.select(container).selectAll('*').remove();

  const margin = { top: 20, right: 30, bottom: 40, left: 50 };
  const width = container.clientWidth - margin.left - margin.right;
  const height = 200 - margin.top - margin.bottom;

  const svg = d3.select(container)
    .append('svg')
    .attr('width', width + margin.left + margin.right)
    .attr('height', height + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // Scales
  const x = d3.scaleTime()
    .domain(d3.extent(data, d => new Date(d.timestamp)) as [Date, Date])
    .range([0, width]);

  const y = d3.scaleLinear()
    .domain([0, d3.max(data, d => d.power_mw) || 1])
    .nice()
    .range([height, 0]);

  // Area generator
  const area = d3.area<HourlyGeneration>()
    .x(d => x(new Date(d.timestamp)))
    .y0(height)
    .y1(d => y(d.power_mw))
    .curve(d3.curveMonotoneX);

  // Line generator
  const line = d3.line<HourlyGeneration>()
    .x(d => x(new Date(d.timestamp)))
    .y(d => y(d.power_mw))
    .curve(d3.curveMonotoneX);

  // Gradient
  const gradient = svg.append('defs')
    .append('linearGradient')
    .attr('id', 'areaGradient')
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%');

  gradient.append('stop')
    .attr('offset', '0%')
    .attr('stop-color', 'var(--accent-color)')
    .attr('stop-opacity', 0.4);

  gradient.append('stop')
    .attr('offset', '100%')
    .attr('stop-color', 'var(--accent-color)')
    .attr('stop-opacity', 0.05);

  // Draw area
  svg.append('path')
    .datum(data)
    .attr('fill', 'url(#areaGradient)')
    .attr('d', area);

  // Draw line
  svg.append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', 'var(--accent-color)')
    .attr('stroke-width', 2)
    .attr('d', line);

  // X axis
  svg.append('g')
    .attr('transform', `translate(0,${height})`)
    .call(d3.axisBottom(x)
      .ticks(6)
      .tickFormat(d => d3.timeFormat('%H:%M')(d as Date))
    )
    .attr('class', 'axis');

  // Y axis
  svg.append('g')
    .call(d3.axisLeft(y)
      .ticks(5)
      .tickFormat(d => `${d} MW`)
    )
    .attr('class', 'axis');

  // Show current time indicator when not playing
  if (highlightIndex === null && currentHourIndex >= 0 && data[currentHourIndex]) {
    const point = data[currentHourIndex];
    const cx = x(new Date(point.timestamp));
    const cy = y(point.power_mw);

    // Current time vertical line
    svg.append('line')
      .attr('x1', cx)
      .attr('x2', cx)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', '#22c55e')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '4,4')
      .attr('opacity', 0.7);

    // Current time dot
    svg.append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 6)
      .attr('fill', '#22c55e')
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // "Now" label
    svg.append('text')
      .attr('x', cx)
      .attr('y', -5)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#22c55e')
      .text('Now');
  }

  // Highlight point during playback
  if (highlightIndex !== null && data[highlightIndex]) {
    const point = data[highlightIndex];
    const cx = x(new Date(point.timestamp));
    const cy = y(point.power_mw);

    svg.append('circle')
      .attr('cx', cx)
      .attr('cy', cy)
      .attr('r', 6)
      .attr('fill', 'var(--accent-color)')
      .attr('stroke', 'white')
      .attr('stroke-width', 2);

    // Vertical line
    svg.append('line')
      .attr('x1', cx)
      .attr('x2', cx)
      .attr('y1', 0)
      .attr('y2', height)
      .attr('stroke', 'var(--accent-color)')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4')
      .attr('opacity', 0.5);
  }
}
